import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import CustomAttributeField from "#src/components/Common/CustomAttributeField";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import useLocalization from "#src/hooks/useLocalization";
import { sanitizeCustomAttributeFormInputs } from "#src/utils/customAttributes";
import {
  Button,
  Card,
  Dialog,
  DropdownInput,
  DropdownInputBare,
  Form,
  KeyValueList,
  Skeleton,
  useForm,
  VariableTextInput,
} from "@validereinc/common-components";
import {
  AssetStatus,
  AssetStatusType,
  AssetType,
  AssetTypeType,
  AttributesToUpdate,
  VariableNameSchema,
} from "@validereinc/domain";
import { z } from "zod";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationUpdateAttributesDetails.module.scss";
import { toTitleCaseString } from "@validereinc/utilities";
import { Workflows } from "@validereinc/domain-controllers";
import { isEmpty } from "es-toolkit/compat";

const { UpdateAttributesSystemActionPrimaryAttributesThatCanBeUpdated } =
  Workflows.view;

const cx = classNames.bind(styles);

type AttributesToUpdateFormType = Omit<
  AttributesToUpdate,
  "custom_attributes"
> & {
  custom_attributes?: Record<
    string,
    string | number | string[] | number[] | undefined
  >;
  primary_attributes?: {
    status?: AssetStatusType | string;
  };
};

const validateAssetIdOrWorkflowVariable = (value: string) => {
  if (z.string().uuid().safeParse(value).success) {
    return true;
  }

  if (VariableNameSchema.safeParse(value).success) {
    return true;
  }

  return "Enter a valid asset UUID or workflow variable (e.g. $VARIABLE_NAME)";
};

const AssetAttributesEditDialog = ({
  onClose,
  asset,
  onSave,
  isAddingNew = false,
  index,
  workflowVariables,
}: {
  onClose: () => void;
  asset: AttributesToUpdate | null;
  onSave: (updatedAsset: AttributesToUpdate, index: number) => void;
  isAddingNew?: boolean;
  index: number;
  workflowVariables: string[];
}) => {
  const { localize } = useLocalization();

  const form = useForm<AttributesToUpdateFormType>({
    values: asset ?? {
      asset_type: AssetType.FACILITY,
      asset_id: "",
      custom_attributes: {},
      primary_attributes: {},
    },
  });

  const [isAddingCustomAttribute, setIsAddingCustomAttribute] = useState(false);
  const [customAttributeToAdd, setCustomAttributeToAdd] = useState("");
  const [isAddingPrimaryAttribute, setIsAddingPrimaryAttribute] =
    useState(false);
  const [primaryAttributeToAdd, setPrimaryAttributeToAdd] = useState("");

  const customAttributeValues = form.watch("custom_attributes");

  const primaryAttributeValues = form.watch("primary_attributes");

  const selectedAssetType = form.watch("asset_type");

  const AssetTypeDropdownOptions = useMemo(
    () =>
      Object.values(AssetType).map((assetType) => ({
        label: localize(assetType),
        value: assetType,
      })),
    [localize]
  );

  const statusOptions = [
    {
      id: AssetStatus.ACTIVE,
      name: toTitleCaseString(AssetStatus.ACTIVE, { intent: "ui" }),
    },
    {
      id: AssetStatus.INACTIVE,
      name: toTitleCaseString(AssetStatus.INACTIVE, {
        intent: "ui",
      }),
    },
    ...(([AssetType.EQUIPMENT, AssetType.DEVICE] as AssetTypeType[]).includes(
      selectedAssetType
    )
      ? [
          {
            id: AssetStatus.DEPRECATED,
            name: toTitleCaseString(AssetStatus.DEPRECATED, { intent: "ui" }),
          },
        ]
      : []),
  ];

  // Reset status to ACTIVE if it's DEPRECATED and the asset type doesn't support it
  useEffect(() => {
    const currentStatus = primaryAttributeValues?.status;
    const doesAssetTypeSupportDeprecated = (
      [AssetType.EQUIPMENT, AssetType.DEVICE] as AssetTypeType[]
    ).includes(selectedAssetType);

    if (
      currentStatus === AssetStatus.DEPRECATED &&
      !doesAssetTypeSupportDeprecated
    ) {
      form.setValue("primary_attributes.status", AssetStatus.ACTIVE);
    }
  }, [selectedAssetType, primaryAttributeValues?.status, form]);

  // Fetch custom attributes based on selected asset type
  const { data: customAttributesData, isLoading: isLoadingCustomAttributes } =
    useListCustomAttributes(
      { filters: { entity_type: selectedAssetType } },
      { enabled: !!selectedAssetType }
    );

  const customAttributes = customAttributesData?.data ?? [];

  const customAttributeDefinitions = Object.fromEntries(
    customAttributes.map((attribute) => [attribute.field_name, attribute])
  );

  const customAttributeFieldsToRender = Object.keys(customAttributeValues ?? {})
    .map((ca) => customAttributeDefinitions[ca])
    .filter(Boolean);

  const handleSave = () => {
    if (form.formState.isValid) {
      const formData = form.getValues();

      const sanitizedCustomAttributes = sanitizeCustomAttributeFormInputs(
        formData.custom_attributes ?? {},
        customAttributes
      );

      const updatedAsset: AttributesToUpdate = {
        ...formData,
        ...(!isEmpty(sanitizedCustomAttributes)
          ? { custom_attributes: sanitizedCustomAttributes }
          : {}),
        ...(!isEmpty(formData.primary_attributes)
          ? { primary_attributes: formData.primary_attributes }
          : {}),
      };

      onSave(updatedAsset, index);
      onClose();
    }
  };

  const customAttributePickerOptions = customAttributes
    .filter(
      (ca) => !Object.keys(customAttributeValues ?? {}).includes(ca.field_name)
    )
    .map((attribute) => ({
      label: attribute.display_name,
      value: attribute.field_name,
    }));

  const primaryAttributePickerOptions =
    UpdateAttributesSystemActionPrimaryAttributesThatCanBeUpdated.filter(
      (attribute) => !primaryAttributeValues?.[attribute]
    ).map((attribute) => ({
      label: toTitleCaseString(attribute, { intent: "ui" }),
      value: attribute,
    }));

  if (!asset) return null;

  return (
    <Dialog
      title={isAddingNew ? "Add New Asset" : "Edit Asset Attributes"}
      isOpen={!!asset}
      onClose={onClose}
      actionRow={[
        <Button
          key="save"
          variant="primary"
          onClick={handleSave}
          disabled={!form.formState.isValid}
        >
          {isAddingNew ? "Add Asset" : "Save"}
        </Button>,
      ]}
    >
      <Form {...form}>
        <div className={cx("editDialog")}>
          <DropdownInput
            name="asset_type"
            label="Asset Type"
            isRequired
            options={AssetTypeDropdownOptions}
            labelKey="label"
            valueKey="value"
          />

          <VariableTextInput
            name="asset_id"
            label="Asset ID"
            isRequired
            isDisabled={!selectedAssetType}
            placeholder="Enter asset UUID"
            suggestions={workflowVariables}
            rules={{
              validate: validateAssetIdOrWorkflowVariable,
            }}
          />

          <div className={cx("title")}>Custom Attributes to Update</div>
          <div className={cx("attributesSection")}>
            <Skeleton isLoading={isLoadingCustomAttributes}>
              {customAttributes.length === 0 && (
                <div className={cx("emptyAttributesState")}>
                  <p>No custom attributes found for this asset type.</p>
                </div>
              )}
              {customAttributeFieldsToRender.length > 0 && (
                <div className={cx("attributeFields")}>
                  {customAttributeFieldsToRender.map((attribute) => (
                    <div
                      key={attribute.field_name}
                      style={{ gap: 8, display: "flex" }}
                    >
                      <CustomAttributeField
                        attribute={{ ...attribute, is_required: true }}
                        name={`custom_attributes.${attribute.field_name}`}
                        style={{ flexGrow: 1 }}
                      />
                      <Button
                        variant="error-outline"
                        icon="trash"
                        size="small"
                        onClick={() => {
                          form.unregister(
                            `custom_attributes.${attribute.field_name}`
                          );
                        }}
                        style={{ marginTop: 24 }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Skeleton>

            {!isAddingCustomAttribute && !isLoadingCustomAttributes && (
              <Button
                variant="outline"
                icon="plus"
                size="small"
                onClick={() => {
                  setIsAddingCustomAttribute(true);
                }}
                disabled={isAddingCustomAttribute}
              >
                New Custom Attribute
              </Button>
            )}
            {isAddingCustomAttribute && (
              <div style={{ display: "flex", gap: 8 }}>
                <DropdownInputBare
                  isFluid
                  value={customAttributeToAdd}
                  onChange={setCustomAttributeToAdd}
                  options={customAttributePickerOptions}
                  labelKey="label"
                  valueKey="value"
                  style={{ flexGrow: 2 }}
                />
                <Button
                  variant="outline"
                  icon="check"
                  size="small"
                  disabled={!customAttributeToAdd}
                  onClick={() => {
                    form.setValue(
                      `custom_attributes.${customAttributeToAdd}`,
                      ""
                    );
                    setIsAddingCustomAttribute(false);
                    setCustomAttributeToAdd("");
                  }}
                >
                  Add
                </Button>
              </div>
            )}
          </div>
          <div className={cx("title")}>Primary Attributes to Update</div>
          <div className={cx("attributesSection")}>
            <Skeleton isLoading={isLoadingCustomAttributes}>
              {primaryAttributeValues?.status && (
                <div className={cx("attributeFields")}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <DropdownInput
                      name="primary_attributes.status"
                      label="Status"
                      options={statusOptions}
                      isMulti={false}
                      isFluid
                      isSearchable={false}
                      isRequired
                      valueKey="id"
                      labelKey="name"
                    />
                    <Button
                      variant="error-outline"
                      icon="trash"
                      size="small"
                      onClick={() => {
                        form.unregister(`primary_attributes.status`);
                      }}
                      style={{ marginTop: 24 }}
                    />
                  </div>
                </div>
              )}
            </Skeleton>
            {!isAddingPrimaryAttribute &&
              !isLoadingCustomAttributes &&
              primaryAttributePickerOptions.length > 0 && (
                <Button
                  variant="outline"
                  icon="plus"
                  size="small"
                  onClick={() => {
                    setIsAddingPrimaryAttribute(true);
                  }}
                  disabled={isAddingPrimaryAttribute}
                >
                  New Primary Attribute
                </Button>
              )}
            {isAddingPrimaryAttribute && (
              <div style={{ display: "flex", gap: 8 }}>
                <DropdownInputBare
                  isFluid
                  value={primaryAttributeToAdd}
                  onChange={setPrimaryAttributeToAdd}
                  options={primaryAttributePickerOptions}
                  labelKey="label"
                  valueKey="value"
                  style={{ flexGrow: 2 }}
                />
                <Button
                  variant="outline"
                  icon="check"
                  size="small"
                  disabled={!primaryAttributeToAdd}
                  onClick={() => {
                    form.setValue(
                      `primary_attributes.${primaryAttributeToAdd}` as keyof AttributesToUpdateFormType["primary_attributes"],
                      AssetStatus.ACTIVE as never
                    );
                    setIsAddingPrimaryAttribute(false);
                    setPrimaryAttributeToAdd("");
                  }}
                >
                  Add
                </Button>
              </div>
            )}
          </div>
        </div>
      </Form>
    </Dialog>
  );
};

const WorkflowConfigurationUpdateAttributesDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const {
    setUpdateAttributes,
    getUpdateAttributes,
    setUpdateAttributesIsValid,
  } = useWorkflowConfigurationFormStore();
  const { getAvailableStepVariables } = useWorkflowGraphState();
  const workflowVariables = getAvailableStepVariables(stepId);

  const storeValues = getUpdateAttributes();

  const { localize } = useLocalization();

  const [localAssets, setLocalAssets] = useState<AttributesToUpdate[]>([]);

  useEffect(() => {
    if (Array.isArray(storeValues?.assets) && storeValues?.assets.length > 0) {
      setLocalAssets(storeValues?.assets);
    }
  }, [JSON.stringify(storeValues?.assets)]);

  const updateLocalAssets = (assets: AttributesToUpdate[]) => {
    setLocalAssets(assets);
    setUpdateAttributes({ assets });
  };

  const [editingDialogProps, setEditDialogProps] = useState<{
    asset: AttributesToUpdate | null;
    index: number;
  }>({ asset: null, index: -1 });

  useEffect(() => {
    const isValid = localAssets.length > 0 && localAssets.length <= 10;
    setUpdateAttributesIsValid(isValid);
  }, [localAssets.length, setUpdateAttributesIsValid]);

  const onDialogClose = () => {
    setEditDialogProps({ asset: null, index: -1 });
  };

  const handleSaveAsset = (updatedAsset: AttributesToUpdate, index: number) => {
    if (index === -1) {
      updateLocalAssets([...localAssets, updatedAsset]);
    } else {
      const updatedAssets = [...localAssets];
      updatedAssets[index] = updatedAsset;
      updateLocalAssets(updatedAssets);
    }

    onDialogClose();
  };

  const getAssetKeyValueData = (asset: AttributesToUpdate) => {
    const customAttributeCount = Object.keys(
      asset.custom_attributes ?? {}
    ).length;
    const hasValidCustomAttributes = customAttributeCount > 0;
    const primaryAttributeCount = Object.keys(
      asset.primary_attributes ?? {}
    ).length;
    const hasValidPrimaryAttributes = primaryAttributeCount > 0;

    return [
      {
        title: "Asset Type",
        value: localize(asset.asset_type),
      },
      {
        title: "Asset ID",
        value: asset.asset_id || (
          <span className={cx("emptyValue")}>Not set</span>
        ),
      },
      {
        title: "Custom Attributes",
        value: hasValidCustomAttributes ? (
          `${customAttributeCount} custom attribute${customAttributeCount !== 1 ? "s" : ""} configured`
        ) : (
          <span className={cx("emptyValue")}>
            No custom attributes configured
          </span>
        ),
      },
      {
        title: "Primary Attributes",
        value: hasValidPrimaryAttributes ? (
          `${primaryAttributeCount} primary attribute${primaryAttributeCount !== 1 ? "s" : ""} configured`
        ) : (
          <span className={cx("emptyValue")}>
            No primary attributes configured
          </span>
        ),
      },
    ];
  };

  const renderAssetPanel = (asset: AttributesToUpdate, index: number) => {
    const keyValueData = getAssetKeyValueData(asset);

    return (
      <div
        className={cx("assetPanelContent")}
        key={`${asset.asset_type}-${asset.asset_id}-${index}`}
      >
        <Card>
          <KeyValueList
            data={keyValueData}
            variant="border"
          />
          <div className={cx("assetActions")}>
            <Button
              size="small"
              variant="outline"
              icon="pencil-simple"
              onClick={() => setEditDialogProps({ asset, index })}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="error"
              icon="trash"
              onClick={() => {
                updateLocalAssets(localAssets.filter((_, i) => i !== index));
              }}
            >
              Remove
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className={cx("container")}>
      <p className={cx("description")}>
        Configure which assets should have their attributes updated by this
        automation. You can add up to 10 assets.
      </p>

      <div>
        <div className={cx("sectionHeader")}>
          <div className={cx("title")}>
            Selected Assets ({localAssets.length}/10)
          </div>
          <Button
            variant="primary"
            icon="plus"
            size="small"
            onClick={() => {
              setEditDialogProps({
                asset: {
                  asset_type: AssetType.FACILITY,
                  asset_id: "",
                  custom_attributes: {},
                  primary_attributes: {},
                },
                index: -1,
              });
            }}
            disabled={localAssets.length >= 10}
          >
            Add Asset
          </Button>
        </div>

        {localAssets.length > 0 ? (
          <div className={cx("assetsList")}>
            {localAssets.map(renderAssetPanel)}
          </div>
        ) : null}
      </div>

      <AssetAttributesEditDialog
        onClose={onDialogClose}
        asset={editingDialogProps.asset}
        onSave={handleSaveAsset}
        index={editingDialogProps.index}
        workflowVariables={workflowVariables}
      />
    </div>
  );
};

export default WorkflowConfigurationUpdateAttributesDetails;
